/**
 * Running the league, for room staff: the same commands as Showdown's setup page (/draft ...), run as you. Setting a
 * league up, deleting it and bringing it back stay on Showdown.
 */
import { getAvailable, type DraftLeague, type DraftPhase, type DraftSettings } from '@ogreclan/league-core';
import type { PokemonData } from '@ogreclan/pokemon-core';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { LeagueProps } from '../pages/league';
import { nameOf } from '../pokemon';
import { ActionButton } from '../ui';

type Setting = {
	label: string, type: 'number' | 'yesno' | 'choice', min?: number, max?: number, choices?: string[], phases: DraftPhase[], help?: string,
};

/** The same settings as Showdown's setup page (server/chat-plugins/draft/index.ts) */
const SETTINGS: { [k in keyof DraftSettings]?: Setting } = {
	type: { label: 'Draft type', type: 'choice', choices: ['points', 'free'], phases: ['setup'], help: 'Free drafts let players pick anything still available.' },
	budget: { label: 'Budget (points drafts)', type: 'number', min: 1, max: 100000, phases: ['setup'] },
	picks: { label: 'Pokémon each player drafts', type: 'number', min: 1, max: 40, phases: ['setup'] },
	minPoints: { label: 'Cheapest auto points', type: 'number', min: 0, max: 1000, phases: ['setup', 'draft'] },
	maxPoints: { label: 'Most expensive auto points', type: 'number', min: 1, max: 1000, phases: ['setup', 'draft'] },
	snake: { label: 'Snake order', type: 'yesno', phases: ['setup'], help: 'The order reverses every round.' },
	shuffle: { label: 'Shuffle the order when the draft starts', type: 'yesno', phases: ['setup'] },
	pickTimer: { label: 'Pick timer (minutes, 0 for none)', type: 'number', min: 0, max: 10080, phases: ['setup', 'draft'] },
	tera: { label: 'Tera Captains', type: 'yesno', phases: ['setup', 'draft'], help: 'Off: Terastallization is whatever the format allows.' },
	teraCaptains: { label: 'Tera Captains per player', type: 'number', min: 1, max: 40, phases: ['setup', 'draft'] },
	teraCaptainMaxPoints: { label: 'Most a Tera Captain can cost (0 for no limit)', type: 'number', min: 0, max: 1000, phases: ['setup', 'draft'] },
	freeAgents: { label: 'Free agent pickups per player', type: 'number', min: 0, max: 100, phases: ['setup', 'draft', 'season'] },
	trades: { label: 'Trades per player', type: 'number', min: 0, max: 100, phases: ['setup', 'draft', 'season'] },
	weeks: { label: 'Season length (weeks)', type: 'number', min: 1, max: 52, phases: ['setup', 'draft'] },
	bestOf: { label: 'Matches are best of', type: 'choice', choices: ['1', '3', '5', '7', '9'], phases: ['setup', 'draft', 'season'] },
};

function settingValue(league: DraftLeague, key: keyof DraftSettings) {
	const value = league.settings[key];
	return typeof value === 'boolean' ? value ? 'yes' : 'no' : `${value}`;
}

function SettingsForm({ league, act }: { league: DraftLeague, act: LeagueProps['act'] }) {
	const keys = Object.keys(SETTINGS) as (keyof DraftSettings)[];
	const initial = () => Object.fromEntries(keys.map(key => [key, settingValue(league, key)]));
	const [values, setValues] = useState<Record<string, string>>(initial);
	useEffect(() => setValues(initial()), [league]);
	const changed = keys.filter(key => values[key] !== settingValue(league, key));
	return <form onSubmit={e => {
		e.preventDefault();
		void act('settings', changed.map(key => `${key}=${values[key]}`).join('|'));
	}}>
		<div class="grid" style="grid-template-columns:repeat(auto-fill,minmax(220px,1fr))">
			{keys.map(key => {
				const setting = SETTINGS[key]!;
				const locked = !setting.phases.includes(league.phase);
				const set = (value: string) => setValues({ ...values, [key]: value });
				return <label key={key} class="field" title={locked ? `Can't be changed any more` : setting.help}>
					{setting.label}
					{setting.type === 'number' ? <input type="number" min={setting.min} max={setting.max} disabled={locked} value={values[key]}
						onInput={e => set((e.target as HTMLInputElement).value)} /> :
						<select disabled={locked} value={values[key]} onChange={e => set((e.target as HTMLSelectElement).value)}>
							{(setting.type === 'yesno' ? ['yes', 'no'] : setting.choices!).map(choice => <option key={choice}>{choice}</option>)}
						</select>}
				</label>;
			})}
		</div>
		<p><button type="submit" class="primary" disabled={!changed.length}>Save {changed.length ? `${changed.length} change${changed.length === 1 ? '' : 's'}` : 'settings'}</button></p>
	</form>;
}

/** A one-box form that runs a command with what's typed */
function CommandForm({ label, placeholder, run, multiline, button, value: initial = '' }: {
	label: ComponentChildren, placeholder: string, run: (text: string) => Promise<boolean>, multiline?: boolean, button: string, value?: string,
}) {
	const [text, setText] = useState(initial);
	useEffect(() => setText(initial), [initial]);
	return <form class="stack" onSubmit={async e => {
		e.preventDefault();
		if (await run(text) && !initial) setText('');
	}}>
		<label class="field">{label}
			{multiline ? <textarea rows={6} placeholder={placeholder} value={text} onInput={e => setText((e.target as HTMLTextAreaElement).value)} /> :
				<input placeholder={placeholder} value={text} onInput={e => setText((e.target as HTMLInputElement).value)} />}
		</label>
		<div><button type="submit" disabled={!text.trim()}>{button}</button></div>
	</form>;
}

function Players({ league, act }: { league: DraftLeague, act: LeagueProps['act'] }) {
	const setup = league.phase === 'setup';
	return <>
		<table><tbody>{league.players.map((p, i) => <tr key={p.id}>
			<td class="num muted">{i + 1}</td>
			<td>{p.name}</td>
			<td class="right nowrap">
				{setup && i > 0 && <ActionButton class="small" title="Move up the draft order" action={() => act('moveplayer', `${p.id}, ${i}`)}>↑</ActionButton>}{' '}
				{setup && i < league.players.length - 1 && <ActionButton class="small" title="Move down" action={() => act('moveplayer', `${p.id}, ${i + 2}`)}>↓</ActionButton>}{' '}
				<ActionButton class="small" action={async () => {
					const name = window.prompt(`${p.name}'s new Showdown name:`, p.name);
					if (name && name.trim() !== p.name) await act('renameplayer', `${p.id}, ${name.trim()}`);
				}}>Rename</ActionButton>{' '}
				{setup && <ActionButton class="small danger" confirm={`Remove ${p.name} from the league?`} action={() => act('removeplayer', p.id)}>Remove</ActionButton>}
			</td>
		</tr>)}</tbody></table>
		{setup && <CommandForm label="Add players (their Showdown names)" placeholder="Larry, Bob, Cat" button="Add"
			run={text => act('addplayers', text.split(/[\n,]/).map(name => name.trim()).filter(Boolean).join(', '))} />}
	</>;
}

function Pool({ league, data, act }: { league: DraftLeague, data: PokemonData | null, act: LeagueProps['act'] }) {
	const [points, setPoints] = useState('');
	const [names, setNames] = useState('');
	const open = league.phase === 'setup' || league.phase === 'draft';
	return <>
		<p>{Object.keys(league.pool).length} Pokémon in the pool{league.phase === 'draft' && `, ${getAvailable(league).length} still available`}.</p>
		{open && <div class="row">
			<ActionButton confirm="Replace the pool with every Pokémon the format allows, priced automatically?" action={() => act('generatepool')}>
				Fill the pool from the format</ActionButton>
			{league.settings.type === 'points' && <ActionButton confirm="Price every Pokémon in the pool automatically? This replaces the points you've set."
				action={() => act('autopoints')}>Price automatically</ActionButton>}
		</div>}
		{open && <>
			<h3>Set points</h3>
			<form class="row" onSubmit={async e => {
				e.preventDefault();
				if (await act('setpoints', `${points}, ${names}`)) setNames('');
			}}>
				<input type="number" min={0} placeholder="Points" value={points} style="width:100px" onInput={e => setPoints((e.target as HTMLInputElement).value)} required />
				<input placeholder="Pokémon, Pokémon… (adds them if they aren't in the pool)" value={names} style="flex:1;min-width:220px"
					onInput={e => setNames((e.target as HTMLInputElement).value)} list="pool-names" required />
				<button type="submit">Set</button>
			</form>
			<datalist id="pool-names">{Object.keys(league.pool).map(id => <option key={id} value={nameOf(data, id)} />)}</datalist>
			<CommandForm label="Take Pokémon out of the pool" placeholder="Pokémon, Pokémon…" button="Remove" run={text => act('removepool', text)} />
			<CommandForm label="Import the pool from a spreadsheet (CSV: name, points)" placeholder={'Garchomp,15\nIncineroar,12'} multiline
				button="Import" run={text => act('importpool', text)} />
		</>}
	</>;
}

function Season({ league, data, act }: { league: DraftLeague, data: PokemonData | null, act: LeagueProps['act'] }) {
	const [player, setPlayer] = useState('');
	const players = <select value={player} onChange={e => setPlayer((e.target as HTMLSelectElement).value)} required>
		<option value="">Player…</option>
		{league.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
	</select>;
	return <>
		<div class="row">
			<span>Week {league.week} of {league.schedule.length}</span>
			<ActionButton disabled={league.week >= league.schedule.length} action={() => act('week', 'next')}>Next week</ActionButton>
		</div>
		<h3>Enter a result</h3>
		<p class="muted small">For a match played somewhere else, or one that needs correcting. Games won by that player, then by their opponent.</p>
		<CommandForm label="Week, winner, score" placeholder={`${league.week}, ${league.players[0]?.name || 'Larry'}, 2-1`} button="Enter result"
			run={text => act('report', text)} />
		<CommandForm label="Clear a result: week, player" placeholder={`${league.week}, ${league.players[0]?.name || 'Larry'}`} button="Clear"
			run={text => act('clearresult', text)} />
		<h3>Fix a roster</h3>
		<form class="row" onSubmit={async e => {
			e.preventDefault();
			const form = e.currentTarget as HTMLFormElement;
			const mon = (form.elements.namedItem('mon') as HTMLInputElement).value;
			const command = (e.submitter as HTMLButtonElement | null)?.value || 'addmon';
			await act(command, `${player}, ${mon}`);
		}}>
			{players}
			<input name="mon" placeholder="Pokémon" list="pool-names-season" required />
			<datalist id="pool-names-season">{Object.keys(league.pool).map(id => <option key={id} value={nameOf(data, id)} />)}</datalist>
			<button type="submit" value="addmon">Add to roster</button>
			<button type="submit" value="removemon">Remove from roster</button>
		</form>
		<p style="margin-top:16px"><ActionButton class="danger" confirm="End the season? The standings become final." action={() => act('endseason', 'confirm')}>
			End the season</ActionButton></p>
	</>;
}

export function ManageTab({ view, data, act }: LeagueProps) {
	const { league } = view;
	return <>
		<p class="muted">These run the same commands as the league's setup page on Showdown, as you. Setting up, deleting and restoring
			a league stay on Showdown (<code>/draft setup</code>, <code>/draft delete</code>, <code>/draft restore</code>).</p>
		<div class="card">
			<h2>Format</h2>
			<CommandForm label="Format, or a challenge code (/challenge format @@@ rules)" placeholder="gen9vgc2025regh" value={league.settings.format}
				button="Save the format" run={text => act('format', text)} />
		</div>
		<div class="card"><h2>Settings</h2><SettingsForm league={league} act={act} /></div>
		<div class="card"><h2>Players</h2><Players league={league} act={act} /></div>
		<div class="card"><h2>Pool</h2><Pool league={league} data={data} act={act} /></div>
		{league.phase === 'setup' && <div class="card">
			<h2>Draft</h2>
			<ActionButton class="primary" confirm="Start the draft now?" action={() => act('start')}>Start the draft</ActionButton>
		</div>}
		{league.phase === 'draft' && <div class="card">
			<h2>Draft</h2>
			<p>Skip, undo and picks for players are on the <a href={`/league/${view.roomid}/draft`}>Draft</a> tab.</p>
			<ActionButton confirm="End the draft and start the season?" action={() => act('end', 'confirm')}>End the draft</ActionButton>
		</div>}
		{league.phase === 'season' && <div class="card"><h2>Season</h2><Season league={league} data={data} act={act} /></div>}
	</>;
}
